interface WeatherData {
  temperature: number;
  humidity: number;
  description: string;
  icon: string;
  rainfall: number;
  forecast: Array<{
    date: string;
    temperature: number;
    humidity: number;
    rainfall: number;
    description: string;
    icon: string;
  }>;
}

interface WeatherError {
  message: string;
}

class WeatherService {
  private apiKey: string;
  private apiUrl: string;

  constructor() {
    this.apiKey = import.meta.env.VITE_OPENWEATHER_API_KEY;
    this.apiUrl = import.meta.env.VITE_OPENWEATHER_API_URL;
  }

  private kelvinToCelsius(kelvin: number): number {
    return Math.round(kelvin - 273.15);
  }

  private async fetchWithErrorHandling(url: string) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Weather data fetch failed');
      }
      return await response.json();
    } catch (error) {
      throw {
        message: error instanceof Error ? error.message : 'Failed to fetch weather data'
      };
    }
  }

  async getCurrentWeather(lat: number, lon: number): Promise<WeatherData | WeatherError> {
    try {
      const url = `${this.apiUrl}/weather?lat=${lat}&lon=${lon}&appid=${this.apiKey}`;
      const data = await this.fetchWithErrorHandling(url);

      return {
        temperature: this.kelvinToCelsius(data.main.temp),
        humidity: data.main.humidity,
        description: data.weather[0].description,
        icon: data.weather[0].icon,
        rainfall: data.rain ? data.rain['1h'] || 0 : 0,
        forecast: [] // Will be populated by getForecast
      };
    } catch (error) {
      return { message: error instanceof Error ? error.message : 'Failed to fetch current weather' };
    }
  }

  async getForecast(lat: number, lon: number): Promise<WeatherData | WeatherError> {
    try {
      const url = `${this.apiUrl}/forecast?lat=${lat}&lon=${lon}&appid=${this.apiKey}`;
      const data = await this.fetchWithErrorHandling(url);

      const forecast = data.list
        .filter((_: any, index: number) => index % 8 === 0) // Get one reading per day
        .map((item: any) => ({
          date: new Date(item.dt * 1000).toLocaleDateString(),
          temperature: this.kelvinToCelsius(item.main.temp),
          humidity: item.main.humidity,
          rainfall: item.rain ? item.rain['3h'] || 0 : 0,
          description: item.weather[0].description,
          icon: item.weather[0].icon
        }));

      const currentWeather = await this.getCurrentWeather(lat, lon);
      if ('message' in currentWeather) {
        throw new Error(currentWeather.message);
      }

      return {
        ...currentWeather,
        forecast
      };
    } catch (error) {
      return { message: error instanceof Error ? error.message : 'Failed to fetch forecast' };
    }
  }

  getIrrigationAdvice(weatherData: WeatherData): string {
    const { temperature, humidity, rainfall, forecast } = weatherData;
    
    // Check if it's currently raining or high chance of rain
    if (rainfall > 0) {
      return "Currently raining. No irrigation needed.";
    }

    // Check next day's forecast
    const tomorrowForecast = forecast[0];
    if (tomorrowForecast && tomorrowForecast.rainfall > 0) {
      return "Rain expected tomorrow. Consider delaying irrigation.";
    }

    // Temperature-based advice
    if (temperature > 30) {
      return "High temperature detected. Consider evening irrigation to minimize evaporation.";
    }

    // Humidity-based advice
    if (humidity > 70) {
      return "High humidity detected. Reduce irrigation to prevent fungal growth.";
    }

    return "Normal conditions. Follow regular irrigation schedule.";
  }
}

export const weatherService = new WeatherService();
export type { WeatherData, WeatherError }; 